import type { YearBundle } from '@budgetx/shared';

const ACCENT = 'var(--accent)';
const VARIABLE_NAMES = new Set(['Dagligvarer', 'Børn & fritid']);
// Daily discretionary allowance, roughly enough for one household's daily
// living expenses until the next payday. Payday is assumed to be the 25th.
const DAILY_RATE = 500;
const PAYDAY = 25;

function isIncome(c: YearBundle['categories'][number]): boolean {
  return c.kind === 'income';
}

function categoryType(
  c: YearBundle['categories'][number],
): 'income' | 'fast' | 'variabel' | 'opsparing' {
  if (isIncome(c)) return 'income';
  if (c.kind === 'opsparing') return 'opsparing';
  if (c.kind === 'variabel' || VARIABLE_NAMES.has(c.name)) return 'variabel';
  return 'fast';
}

const MONTHS_LOWER = [
  'januar',
  'februar',
  'marts',
  'april',
  'maj',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'december',
];

export interface CategoryBar {
  name: string;
  budget: number;
  pct: number; // 0-100, sized relative to the max budget in the set
  color: string;
}

export interface SavingsItem {
  label: string;
  value: number;
}

export interface OverblikVm {
  forbrugsramme: number;
  forbrugsrammeSub: string;
  opsparingerItems: SavingsItem[];
  opsparingerTotal: number;
  depoterItems: SavingsItem[];
  depoterTotal: number;
  savingsGrandTotal: number;
  periodLabel: string;
  fastTotal: number;
  categoryBars: CategoryBar[];
  insight: string;
}

function sumAtMonth(
  bundle: YearBundle,
  monthIdx: number,
  kinds: (c: YearBundle['categories'][number]) => boolean,
  useActual: boolean,
): number {
  let total = 0;
  for (const c of bundle.categories) {
    if (!kinds(c)) continue;
    for (const it of c.items) {
      if (useActual) {
        const a = it.actuals[monthIdx];
        if (a !== null && a !== undefined) total += a;
      } else {
        total += it.budgets[monthIdx] ?? 0;
      }
    }
  }
  return total;
}

function sumYTD(
  bundle: YearBundle,
  upToMonth: number,
  kinds: (c: YearBundle['categories'][number]) => boolean,
  useActual: boolean,
): number {
  let total = 0;
  for (let m = 0; m <= upToMonth; m++) {
    total += sumAtMonth(bundle, m, kinds, useActual);
  }
  return total;
}

function daysUntilNextPayday(now: Date): { days: number; monthName: string } {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target =
    now.getDate() < PAYDAY
      ? new Date(now.getFullYear(), now.getMonth(), PAYDAY)
      : new Date(now.getFullYear(), now.getMonth() + 1, PAYDAY);
  const days = Math.max(0, Math.round((target.getTime() - today.getTime()) / 86_400_000));
  return { days, monthName: MONTHS_LOWER[target.getMonth()]! };
}

export function deriveOverblik(
  bundle: YearBundle,
  monthIdx: number,
  period: 'month' | 'next' | 'ytd',
): OverblikVm {
  const nextIdx = Math.min(11, monthIdx + 1);

  // Til forbrug: 500 kr/day × days-to-next-25th
  const { days, monthName } = daysUntilNextPayday(new Date());
  const forbrugsramme = DAILY_RATE * days;
  const forbrugsrammeSub = `${DAILY_RATE} kr/dag × ${days} dage til 25. ${monthName}`;

  // Latest non-null balance across the whole year (any month) — "sidste
  // indtastede" beløb, uanset om det er før eller efter den valgte måned.
  const latestAny = (amounts: (number | null)[]): number => {
    for (let m = amounts.length - 1; m >= 0; m--)
      if (amounts[m] !== null && amounts[m] !== undefined) return amounts[m] as number;
    return 0;
  };

  // Opsparinger: all goals + all accounts (both are savings-shaped: name + monthly amounts).
  const opsparingerItems: SavingsItem[] = [
    ...bundle.goals.map((g) => ({ label: g.name, value: latestAny(g.amounts) })),
    ...bundle.accounts.map((a) => ({ label: a.name, value: latestAny(a.amounts) })),
  ];
  const opsparingerTotal = opsparingerItems.reduce((s, x) => s + x.value, 0);

  // Depoter: aktier & andre investeringer.
  const depoterItems: SavingsItem[] = bundle.depots.map((d) => ({
    label: d.name,
    value: latestAny(d.amounts),
  }));
  const depoterTotal = depoterItems.reduce((s, x) => s + x.value, 0);
  const savingsGrandTotal = opsparingerTotal + depoterTotal;

  // Forventede faste udgifter: budget totals for fast categories only, sized
  // relative to the largest fast category in the set.
  const fastCatBudgets = bundle.categories
    .filter((c) => categoryType(c) === 'fast')
    .map((c) => {
      const budget =
        period === 'ytd'
          ? sumYTD(bundle, monthIdx, (x) => x.id === c.id, false)
          : period === 'next'
            ? sumAtMonth(bundle, nextIdx, (x) => x.id === c.id, false)
            : sumAtMonth(bundle, monthIdx, (x) => x.id === c.id, false);
      return { name: c.name, budget };
    });
  const fastMax = Math.max(1, ...fastCatBudgets.map((c) => c.budget));
  const categoryBars: CategoryBar[] = fastCatBudgets.map((c) => ({
    name: c.name,
    budget: c.budget,
    pct: Math.round((c.budget / fastMax) * 100),
    color: ACCENT,
  }));
  const fastTotal = fastCatBudgets.reduce((s, c) => s + c.budget, 0);
  const periodLabel =
    period === 'ytd' ? 'År til dato' : period === 'next' ? 'Næste måned' : 'Denne måned';

  // Guiden bemærker: worst overrun on a fast category this month
  const currentMonthOverruns = bundle.categories
    .filter((c) => categoryType(c) === 'fast')
    .map((c) => {
      const budget = sumAtMonth(bundle, monthIdx, (x) => x.id === c.id, false);
      const actual = sumAtMonth(bundle, monthIdx, (x) => x.id === c.id, true);
      return { name: c.name, budget, actual, over: actual - budget };
    })
    .filter((c) => c.over > 0)
    .sort((a, b) => b.over - a.over);
  const worst = currentMonthOverruns[0];
  const insight = worst
    ? `${worst.name} ligger ${Math.round((worst.over / Math.max(1, worst.budget)) * 100)} % over budget og trækker forbruget ${Math.round(worst.over).toLocaleString('da-DK')} kr. op. Ellers holder I fint.`
    : 'Ingen større overskridelser lige nu. Godt gået.';

  return {
    forbrugsramme,
    forbrugsrammeSub,
    opsparingerItems,
    opsparingerTotal,
    depoterItems,
    depoterTotal,
    savingsGrandTotal,
    periodLabel,
    fastTotal,
    categoryBars,
    insight,
  };
}
